'use client';

import type { Table } from '@tanstack/react-table';
import { MoreHorizontal, Settings } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  DataTableActionBar,
  DataTableActionBarAction,
  DataTableActionBarSelection,
} from '@/components/data-table/data-table-action-bar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Store } from '@/lib/api/stores';

import { ExportSelectedStoresModal } from './export-selected-stores-modal';
import { BulkUpdateCurrenciesModal } from './bulk-update-currencies-modal';
import { BulkSyncIntegrationsModal } from './bulk-sync-integrations-modal';
import { BulkUpdateSettingsModal } from './bulk-update-settings-modal';

interface StoresTableActionBarProps {
  table: Table<Store>;
  onRefetch?: () => void;
}

export function StoresTableActionBar({ table, onRefetch }: StoresTableActionBarProps) {
  const { t } = useTranslation('stores');
  const rows = table.getFilteredSelectedRowModel().rows;
  const [showExportDialog, setShowExportDialog] = React.useState(false);
  const [showCurrenciesDialog, setShowCurrenciesDialog] = React.useState(false);
  const [showSyncDialog, setShowSyncDialog] = React.useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = React.useState(false);
  const [settingsModalStores, setSettingsModalStores] = React.useState<Store[]>([]);
  const [moreMenuOpen, setMoreMenuOpen] = React.useState(false);

  const selectedStores = rows.map((row) => row.original);

  const handleExportDialogSuccess = React.useCallback(() => {
    table.resetRowSelection();
  }, [table]);

  const handleCurrenciesDialogSuccess = React.useCallback(() => {
    table.resetRowSelection();
    onRefetch?.();
  }, [table, onRefetch]);

  const handleSyncDialogSuccess = React.useCallback(() => {
    table.resetRowSelection();
  }, [table]);

  const handleOpenSettingsDialog = React.useCallback(() => {
    setSettingsModalStores(selectedStores);
    setShowSettingsDialog(true);
  }, [selectedStores]);

  const openMoreMenuAction = React.useCallback((action: () => void) => {
    setMoreMenuOpen(false);
    action();
  }, []);

  React.useEffect(() => {
    if (showExportDialog || showCurrenciesDialog || showSyncDialog || showSettingsDialog) {
      setMoreMenuOpen(false);
    }
  }, [showExportDialog, showCurrenciesDialog, showSyncDialog, showSettingsDialog]);

  const handleSettingsDialogSuccess = React.useCallback(() => {
    table.resetRowSelection();
    onRefetch?.();
  }, [table, onRefetch]);

  return (
    <>
      <DataTableActionBar table={table} visible={rows.length > 0}>
        <DataTableActionBarSelection table={table} />
        <Separator orientation="vertical" className="hidden data-[orientation=vertical]:h-5 sm:block" />
        <div className="flex items-center gap-1.5">
          <DropdownMenu open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
            <DropdownMenuTrigger asChild>
              <DataTableActionBarAction tooltip={t('table.actionBar.moreActionsTooltip')}>
                <MoreHorizontal className="mr-2 h-4 w-4" />
                {t('table.actionBar.more')}
              </DataTableActionBarAction>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              <DropdownMenuItem onSelect={() => openMoreMenuAction(() => setShowExportDialog(true))}>
                {t('table.actionBar.exportSelected')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openMoreMenuAction(() => setShowCurrenciesDialog(true))}>
                {t('table.actionBar.updateCurrencies')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openMoreMenuAction(() => setShowSyncDialog(true))}>
                {t('table.actionBar.syncIntegrations')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => openMoreMenuAction(handleOpenSettingsDialog)}>
                <Settings className="mr-2 h-4 w-4" />
                {t('table.actionBar.bulkSettings')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </DataTableActionBar>

      <ExportSelectedStoresModal
        stores={selectedStores}
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        onSuccess={handleExportDialogSuccess}
      />
      <BulkUpdateCurrenciesModal
        stores={selectedStores}
        open={showCurrenciesDialog}
        onClose={() => setShowCurrenciesDialog(false)}
        onSuccess={handleCurrenciesDialogSuccess}
      />
      <BulkSyncIntegrationsModal
        stores={selectedStores}
        open={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        onSuccess={handleSyncDialogSuccess}
      />
      {showSettingsDialog ? (
        <BulkUpdateSettingsModal
          stores={settingsModalStores}
          open={showSettingsDialog}
          onClose={() => setShowSettingsDialog(false)}
          onSuccess={handleSettingsDialogSuccess}
        />
      ) : null}
    </>
  );
}
